import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Pagination } from "@/shared/ui/Pagination";
import {
  costEnginePrice,
  costEngineBreakeven,
  listChannelConfig,
  RETAIL_FORMULA_NOTE,
  type CostSell,
} from "@/services/pricing";
import { useCan } from "@/auth/permissions";
import { ChannelOverrideDrawer } from "./ChannelOverrideDrawer";
import { PriceHover } from "./PriceHover";
import { listProducts } from "@/services/products";
import type { ProductRead } from "@/services/types/products";
import { PageShell, LoadingState } from "@/shared/ui/PageHeader";
import { Card } from "@/shared/ui/Card";
import { PricingSubnav } from "./PricingSubnav";

const usd = (c?: number | null) => (c == null ? "—" : `$${Math.round(c / 100).toLocaleString()}`);
const productHref = (id: string) => `/admin/pricing/product/${id}`;

/** 查询失败态 + 重试（杜绝 API 失败伪装成永久加载/空表）。 */
function QueryError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center gap-2 py-8 text-[13px] text-slate-500">
      <span>加载失败 · 请重试</span>
      <button onClick={onRetry} className="rounded-md border border-slate-300 px-3 py-1 text-[12px] font-medium text-blue-600 hover:bg-blue-50">重试</button>
    </div>
  );
}

type Row = { p: ProductRead; cs: CostSell | null };

const ch = (cs: CostSell | null, k: string) => cs?.channels.find((c) => c.channel === k);

/** 套餐毛利率 → 红黄绿状态（按同行净毛利率，结果值；MARKUP-1 起由加成率推出，不是配置）。
 * PMODE-1 · manual 产品不走红黄绿——手填成本是产品经理自己定的数，不是「引擎算出来的
 * 健康度」，红黄绿的语义对它不成立，统一显灰 pill「手填 $X」。
 */
function statusOf(
  marginPct: number | null | undefined,
  packageCostCents?: number | null,
  pricingMode?: string,
): { cn: string; dot: string; text: string } {
  if (pricingMode === "manual") {
    return { cn: `手填 ${usd(packageCostCents)}`, dot: "bg-slate-400", text: "text-slate-600" };
  }
  if (marginPct == null) return { cn: "—", dot: "bg-slate-300", text: "text-slate-500" };
  // 2026-09-16 补：套餐成本 0 = 引擎没拿到任何成本（车没配日成本 / 没绑车型），不是"健康"。
  // 公司机 3 个产品全 $0 却显示 🟢 健康，同事看不出哪里没配。
  if (!packageCostCents) return { cn: "成本未配置", dot: "bg-rose-500", text: "text-rose-600" };
  if (marginPct < 0) return { cn: "套餐亏 · 靠必付补", dot: "bg-rose-500", text: "text-rose-600" };
  if (marginPct < 10) return { cn: "薄利", dot: "bg-amber-500", text: "text-amber-600" };
  return { cn: "健康", dot: "bg-emerald-500", text: "text-emerald-600" };
}

/** 保本预警行 · 子查 breakeven 求保本点（首个 green 团人数）· 整行可点进详情。 */
function WarnRow({ row }: { row: Row }) {
  const navigate = useNavigate();
  const beQ = useQuery({
    queryKey: ["pricing", "be-min", row.p.id],
    queryFn: () => costEngineBreakeven(row.p.id, { occupancy: 2 }),
  });
  const green = beQ.data?.find((r) => r.status === "green");
  const bep = beQ.isLoading ? "…" : green ? `${green.pax} 人` : "满载仍亏";
  const b2b = ch(row.cs, "b2b");
  const st = statusOf(b2b?.margin_pct, row.cs?.package_cost_per_person_cents, row.p.pricing_mode);
  return (
    <tr className="cursor-pointer border-t border-slate-100 hover:bg-slate-50" onClick={() => navigate(productHref(row.p.id))}>
      <td className="px-4 py-3"><span className="font-medium text-blue-600 hover:underline">{row.p.name_cn}</span><span className="ml-2 text-[11px] text-slate-400">{row.p.code}</span></td>
      <td className="px-4 py-3 text-slate-600">{row.cs?.vehicle_seats ? `${row.cs.vehicle_seats} 座${row.cs.vehicle_label || ""}` : "—"}</td>
      <td className="px-4 py-3 text-slate-600">{bep}</td>
      <td className="px-4 py-3 text-slate-400" title="待接订单数据（本月发团团均人数）">—</td>
      <td className="px-4 py-3"><span className="inline-flex items-center gap-1.5"><span className={`h-2 w-2 rounded-full ${st.dot}`} /><span className={st.text}>{st.cn}</span></span></td>
    </tr>
  );
}

/** 售价管理中心 · 售价总览（/admin/pricing · 成本引擎看板 · canvas page-60）。 */
export function PricingCenterPage() {
  const navigate = useNavigate();
  const chQ = useQuery({ queryKey: ["pricing", "channel-config"], queryFn: listChannelConfig });
  const prodsQ = useQuery({ queryKey: ["products", "list-min"], queryFn: () => listProducts({ limit: 200 }) });
  const rowsQ = useQuery({
    queryKey: ["pricing", "price-all", prodsQ.data?.length],
    enabled: !!prodsQ.data,
    queryFn: async (): Promise<Row[]> =>
      Promise.all(
        (prodsQ.data ?? []).map((p) =>
          costEnginePrice({ product_id: p.id, pax: p.max_pax_per_departure, occupancy: 2 })
            .then((cs) => ({ p, cs }))
            .catch(() => ({ p, cs: null as CostSell | null })),
        ),
      ),
  });

  const rows = rowsQ.data ?? [];
  const b2bCfg = chQ.data?.find((c) => c.channel === "b2b");
  const directCfg = chQ.data?.find((c) => c.channel === "direct");
  const socialCfg = chQ.data?.find((c) => c.channel === "social");
  const apiCfg = chQ.data?.find((c) => c.channel === "api");
  const b2bComm = b2bCfg ? `${+Number(b2bCfg.commission_pct).toFixed(2)}%` : "—";   // CHOVR-1 · 佣金比例读系统设置，不再写死 30%
  const canAdjust = useCan("pricing.channel_config.update");
  const canBreakeven = useCan("pricing.cost.view");   // RBACFIX-1 · 保本接口需要成本查看权限：没有就不显示「保本预警」页签、不请求
  const [adjustId, setAdjustId] = useState<string | null>(null);
  const [onlyOverride, setOnlyOverride] = useState(false);

  // ── 合并表（2 tab 切换 · 共用搜索/状态筛选/分页 · 需关注优先排序 · 2026-07-17 用户拍板）
  const [tabRaw, setTab] = useState<"cost" | "warn">("cost");
  const tab = tabRaw === "warn" && !canBreakeven ? "cost" : tabRaw;
  const [q, setQ] = useState("");
  const [sevFilter, setSevFilter] = useState<"all" | 0 | 1 | 2>("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  useEffect(() => { setPage(1); }, [q, sevFilter, tab, pageSize, onlyOverride]);

  // sev: 0=套餐亏(红) 1=薄利(黄) 2=健康(绿) 3=无数据 · 需关注优先=按 sev 升序·同档毛利低者在前
  const filtered = useMemo(() => {
    const kw = q.trim().toLowerCase();
    return rows
      .map((r) => {
        const m = ch(r.cs, "b2b")?.margin_pct ?? null;
        // 成本 0 归到红档（套餐亏筛选也能筛出来）——它比亏更急：根本没算出来
        const sev = m == null ? 3 : !r.cs?.package_cost_per_person_cents ? 0 : m < 0 ? 0 : m < 10 ? 1 : 2;
        return { ...r, margin: m, sev };
      })
      .filter((r) => sevFilter === "all" || r.sev === sevFilter)
      .filter((r) => !onlyOverride || !!r.cs?.has_channel_override)
      .filter((r) => !kw || r.p.name_cn.toLowerCase().includes(kw) || r.p.code.toLowerCase().includes(kw))
      .sort((a, b) => a.sev - b.sev || (a.margin ?? 9999) - (b.margin ?? 9999));
  }, [rows, q, sevFilter, onlyOverride]);
  const overrideCount = rows.filter((r) => r.cs?.has_channel_override).length;
  const pageRows = useMemo(
    () => filtered.slice((page - 1) * pageSize, page * pageSize),
    [filtered, page, pageSize],
  );

  // KPI 聚合（成本可见角色才有 margin）
  const margins = rows.map((r) => ch(r.cs, "b2b")?.margin_pct).filter((m): m is number => m != null);
  const avgMargin = margins.length ? Math.round(margins.reduce((a, b) => a + b, 0) / margins.length) : null;
  const belowBreak = margins.filter((m) => m < 10).length;
  const activeCount = rows.filter((r) => r.p.status === "published").length;

  return (
    <PageShell
      title="售价总览"
      subtitle="成本引擎实时核算 · 红黄绿保本预警 · 同行 / 散客双轨定价"
    >
      <PricingSubnav />
      <div className="text-[12px] text-slate-400">售价管理中心 › 售价总览 · 成本 / 毛利率 仅 持有「成本查看」权限的角色可见（老板 · 财务 · 运营经理 · 超管） · 结算价 = 成本 × (1 + 成本加成率)；同行建议零售价 = 结算价 × (1 + 同行建议加价率)；API 平台价 = 结算价 ÷ (1 − 平台佣金率)</div>

      {/* KPI */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="平均套餐毛利率" value={avgMargin == null ? "—" : `${avgMargin}%`} sub="同行口径 · 未含必付利润" />
        <StatCard label="保本线下产品" value={String(belowBreak)} sub="需关注 · 多为小团 / 塞纳线" tone={belowBreak > 0 ? "warn" : undefined} />
        <StatCard label="必付利润贡献（本月）" value="—" sub="利润中心 · 待接本月发团数据" />
        <StatCard label="在售产品" value={String(activeCount)} sub={`共 ${rows.length} 个产品`} />
      </div>

      {/* 渠道毛利对比 */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {/* RBACFIX-2 · 没有「成本查看」权限：大数字换成佣金率（同行建议加价率 / 平台佣金率），不显示成本加成率与毛利率；字段缺失时不显示 0% */}
        <Card title="同行 B2B" action={<span className="text-[11px] font-medium text-slate-400">{canBreakeven ? "成本加成率" : "同行建议加价率"}</span>}>
          <div className="text-[32px] font-bold text-slate-900">{canBreakeven ? (b2bCfg?.markup_pct == null ? "—" : `${b2bCfg.markup_pct}%`) : b2bComm}</div>
          <div className="mt-1 text-[12px] text-slate-500">{canBreakeven && b2bCfg?.net_margin_pct != null ? `≈ 毛利率 ${+Number(b2bCfg.net_margin_pct).toFixed(2)}% · ` : ""}{canBreakeven ? `同行建议加价率 ${b2bComm}（建议零售价 = 结算价 + 加价）` : "建议零售价 = 结算价 + 加价"} · 固定价目表（全年统一价）</div>
        </Card>
        <Card title="散客 直客" action={canBreakeven ? <span className="text-[11px] font-medium text-slate-400">成本加成率</span> : undefined}>
          <div className="text-[32px] font-bold text-slate-900">{canBreakeven ? (directCfg?.markup_pct == null ? "—" : `${directCfg.markup_pct}%`) : "无佣金"}</div>
          <div className="mt-1 text-[12px] text-slate-500">{canBreakeven && directCfg?.net_margin_pct != null ? `≈ 毛利率 ${+Number(directCfg.net_margin_pct).toFixed(2)}% · ` : ""}无佣金 · 实时动态报价 · 可促销下探（≥ 同行终端价）</div>
        </Card>
        <Card title="社媒" action={canBreakeven ? <span className="text-[11px] font-medium text-slate-400">成本加成率</span> : undefined}>
          <div className="text-[32px] font-bold text-slate-900" data-testid="social-margin">{canBreakeven ? (socialCfg?.markup_pct == null ? "—" : `${socialCfg.markup_pct}%`) : "无佣金"}</div>
          <div className="mt-1 text-[12px] text-slate-500">{canBreakeven && socialCfg?.net_margin_pct != null ? `≈ 毛利率 ${+Number(socialCfg.net_margin_pct).toFixed(2)}% · ` : ""}无佣金 · 社媒推广落地价</div>
        </Card>
        <Card title="API 平台" action={<span className="text-[11px] font-medium text-slate-400">{canBreakeven ? "成本加成率" : "平台佣金率"}</span>}>
          <div className="text-[32px] font-bold text-slate-900" data-testid="api-margin">{canBreakeven ? (apiCfg?.markup_pct == null ? "—" : `${apiCfg.markup_pct}%`) : apiCfg ? `${+Number(apiCfg.commission_pct).toFixed(2)}%` : "—"}</div>
          <div className="mt-1 text-[12px] text-slate-500">{canBreakeven && apiCfg?.net_margin_pct != null ? `≈ 毛利率 ${+Number(apiCfg.net_margin_pct).toFixed(2)}% · ` : ""}GYG / Viator 等 · 平台抽 {apiCfg ? `${+Number(apiCfg.commission_pct).toFixed(2)}%` : "—"} 佣金</div>
        </Card>
      </div>

      {/* 产品成本与保本 · 2 tab 合并表（成本概览 / 保本预警 · 共用筛选+分页 · 需关注优先） */}
      <Card
        title="产品成本与保本"
        action={<span className="text-[11px] text-slate-400">🔒 成本 / 毛利列需「成本查看」权限</span>}
      >
        {/* tab 切换 */}
        <div className="mb-3 flex gap-1 border-b border-slate-200">
          {([["cost", "💰 成本概览"], ["warn", "⚠️ 保本预警 · 按团型"]] as const).filter(([k]) => k === "cost" || canBreakeven).map(([k, label]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={`border-b-2 px-4 py-2 text-[13px] font-medium ${tab === k ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* 工具条：搜索 + 状态筛选 */}
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="搜索产品名 / 编码..."
            className="h-8 w-56 rounded-md border border-slate-300 px-3 text-[13px] focus:border-blue-500 focus:outline-none"
          />
          {([["all", "全部"], [0, "🔴 套餐亏"], [1, "🟡 薄利"], [2, "🟢 健康"]] as const).map(([k, label]) => (
            <button
              key={String(k)}
              onClick={() => setSevFilter(k as typeof sevFilter)}
              className={`rounded-full px-3 py-1 text-xs font-medium ${sevFilter === k ? "bg-blue-600 text-white" : "border border-slate-300 bg-white text-slate-600 hover:bg-slate-50"}`}
            >
              {label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setOnlyOverride((v) => !v)}
            data-testid="filter-override"
            className={`rounded-full px-3 py-1 text-xs font-medium ${onlyOverride ? "bg-violet-600 text-white" : "border border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100"}`}
          >
            单独设置 {overrideCount}
          </button>
          <span className="ml-auto text-[11px] text-slate-400">需关注优先排序 · 共 {filtered.length} 个产品</span>
        </div>

        {prodsQ.isError ? (
          <QueryError onRetry={() => prodsQ.refetch()} />
        ) : rowsQ.isLoading ? (
          <LoadingState />
        ) : tab === "warn" ? (
          <div className="overflow-hidden rounded-lg border border-slate-200">
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                <tr><th className="px-4 py-3 text-left">产品</th><th className="px-4 py-3 text-left">车型</th><th className="px-4 py-3 text-left">保本点</th><th className="px-4 py-3 text-left">团均人数</th><th className="px-4 py-3 text-left">状态</th></tr>
              </thead>
              <tbody>{pageRows.map((r) => <WarnRow key={r.p.id} row={r} />)}</tbody>
            </table>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full min-w-[1180px] text-[13px]">
              <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3 text-left">产品</th>
                  <th className="px-4 py-3 text-left">车型</th>
                  <th className="px-4 py-3 text-left">套餐成本/人</th>
                  <th className="px-4 py-3 text-left" title={`${RETAIL_FORMULA_NOTE}（加价率以产品的单独设置为准）；同行站显示、下单扣额度的是右边那列「同行结算价」`}>同行建议零售价(2人间 · 结算价 + 加价)</th>
                  <th className="px-4 py-3 text-left" title="同行站显示的是结算价（建议零售价 = 它 × (1 + 同行建议加价率)）">同行结算价(2人间)</th>
                  <th className="px-4 py-3 text-left">散客价(2人间)</th>
                  <th className="px-4 py-3 text-left" title="GYG 等平台上客人付的价（= 结算价 ÷ (1 − 平台佣金率)，平台再按佣金抽成）；已并入必付费">API 平台价(2人间)</th>
                  <th className="px-4 py-3 text-left">必付/人</th>
                  <th className="px-4 py-3 text-left">毛利率 同/散</th>
                  <th className="px-4 py-3 text-left" title="全局 = 跟随渠道系数页；单独设置 = 这个产品有自己的系数">系数</th>
                  <th className="px-4 py-3 text-left">状态</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {pageRows.map((r) => {
                  const b2b = ch(r.cs, "b2b");
                  const direct = ch(r.cs, "direct");
                  const api = ch(r.cs, "api");
                  const custom = !!r.cs?.has_channel_override;
                  const st = statusOf(b2b?.margin_pct, r.cs?.package_cost_per_person_cents, r.p.pricing_mode);
                  return (
                    <tr key={r.p.id} className={`cursor-pointer border-t border-slate-100 hover:bg-slate-50 ${custom ? "bg-[#FCFBFF]" : ""}`} onClick={() => navigate(productHref(r.p.id))}>
                      <td className="px-4 py-3 font-medium">
                        <Link to={productHref(r.p.id)} className="text-blue-600 hover:underline" onClick={(e) => e.stopPropagation()}>{r.p.name_cn}</Link>
                        <span className="ml-2 text-[11px] text-slate-400">{r.p.code}</span>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{r.cs?.vehicle_seats ? `${r.cs.vehicle_seats} 座` : "—"}</td>
                      <td className="px-4 py-3 text-slate-600">{usd(r.cs?.package_cost_per_person_cents)}</td>
                      <td className="px-4 py-3 font-semibold text-slate-900"><PriceHover productId={r.p.id}>{usd(b2b?.list_price_cents)}</PriceHover></td>
                      <td className="px-4 py-3 font-semibold text-blue-700"><PriceHover productId={r.p.id}>{usd(b2b?.company_net_cents)}</PriceHover></td>
                      <td className="px-4 py-3 text-slate-600"><PriceHover productId={r.p.id}>{usd(direct?.list_price_cents)}</PriceHover></td>
                      <td className="px-4 py-3 font-semibold text-slate-900" data-testid="api-price"><PriceHover productId={r.p.id}>{usd(api?.list_price_cents)}</PriceHover></td>
                      <td className="px-4 py-3 text-emerald-600">{usd(r.cs?.mandatory_fee_cents)}</td>
                      <td className="px-4 py-3 text-slate-600">{b2b?.margin_pct == null ? "—" : `${b2b.margin_pct}% / ${direct?.margin_pct ?? "—"}%`}</td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <span className={`inline-flex h-5 items-center rounded-full px-2 text-[11px] font-semibold ${custom ? "bg-violet-50 text-violet-700" : "bg-slate-100 text-slate-600"}`}>{custom ? "单独设置" : "全局"}</span>
                        {r.cs?.price_source && <div className="mt-0.5 text-[11px] text-slate-500" data-testid={`source-${r.p.code}`} title="同行结算价的来源（客户专属价在「客户专属价」页看；同行站不显示来源）">{r.cs.price_source.label}</div>}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3"><span className="inline-flex items-center gap-1.5"><span className={`h-2 w-2 rounded-full ${st.dot}`} /><span className={st.text}>{st.cn}</span></span></td>
                      <td className="whitespace-nowrap px-4 py-3 text-right">
                        {canAdjust && (
                          <button
                            type="button"
                            data-testid={`adjust-${r.p.code}`}
                            onClick={(e) => { e.stopPropagation(); setAdjustId(r.p.id); }}
                            className="h-7 rounded-md border border-blue-100 bg-blue-50 px-2.5 text-[12px] font-medium text-blue-700 hover:bg-blue-100"
                          >
                            调整价格
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!rowsQ.isLoading && !prodsQ.isError && (
          <div className="mt-3">
            <Pagination
              page={page}
              pageSize={pageSize}
              pageCount={pageRows.length}
              total={filtered.length}
              onPage={(p) => setPage(Math.max(1, Math.min(p, Math.max(1, Math.ceil(filtered.length / pageSize)))))}
              onPageSize={setPageSize}
              pageSizeOptions={[30, 60]}
            />
          </div>
        )}
        <div className="mt-2 text-[11px] text-slate-400">💡 套餐成本 = 车队÷N + 房价÷住房人数（门票过路账不计）· {RETAIL_FORMULA_NOTE}（全局同行建议加价率 {b2bComm}，单独设置的产品以自己的为准），同行站显示的是「同行结算价」 · 散客价无佣金 · API 平台价 = GYG 等平台上客人付的价（平台再按佣金抽成） · 紫色「单独设置」= 这个产品有自己的系数，不跟随全局 · 点「调整系数」打开右侧面板 · 点产品名看三桶 + 保本红黄绿 · 保本预警 tab 的团均人数待接订单数据。</div>
      </Card>
      {adjustId && <ChannelOverrideDrawer productId={adjustId} onClose={() => setAdjustId(null)} />}
    </PageShell>
  );
}

function StatCard({ label, value, sub, tone }: { label: string; value: string; sub: string; tone?: "warn" }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-[12px] font-medium text-slate-500">{label}</div>
      <div className={`mt-2 text-[26px] font-bold leading-none ${tone === "warn" ? "text-rose-500" : "text-slate-900"}`}>{value}</div>
      <div className="mt-2 text-[11px] text-slate-500">{sub}</div>
    </div>
  );
}
